import { useEffect, useState } from 'react';
import { ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { listMfaFactors, verifyTotpCode } from '@/lib/mfa';

export function MfaSignInChallenge({
  onVerified,
  onSignOut,
}: {
  onVerified: () => void;
  onSignOut: () => void;
}) {
  const [factorId, setFactorId] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadFactor = async () => {
    setLoading(true);
    setError(null);
    try {
      const factors = await listMfaFactors();
      const verified = factors.find((factor) => factor.status === 'verified');
      if (!verified) throw new Error('No verified authenticator is available. Contact support for account recovery.');
      setFactorId(verified.id);
    } catch (cause) {
      setFactorId(null);
      setError(cause instanceof Error ? cause.message : 'Could not check your authenticator. Try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadFactor();
  }, []);

  const verify = async () => {
    if (!factorId || code.length !== 6 || verifying) return;
    setVerifying(true);
    setError(null);
    try {
      await verifyTotpCode(factorId, code);
      onVerified();
    } catch (cause) {
      setCode('');
      setError(cause instanceof Error ? cause.message : 'Verification failed. Try again.');
    } finally {
      setVerifying(false);
    }
  };

  return (
    <main className="flex min-h-dvh items-center justify-center p-4">
      <Card className="w-full max-w-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5" />
            Verify it is you
          </CardTitle>
          <CardDescription>
            Enter the six-digit code from the authenticator app linked to your account.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {loading ? (
            <p role="status">Checking your authenticator…</p>
          ) : factorId ? (
            <>
              <InputOTP maxLength={6} value={code} onChange={setCode} autoFocus>
                <InputOTPGroup>
                  {[0, 1, 2, 3, 4, 5].map((index) => <InputOTPSlot key={index} index={index} />)}
                </InputOTPGroup>
              </InputOTP>
              <Button onClick={verify} disabled={code.length !== 6 || verifying} className="w-full">
                {verifying ? 'Verifying…' : 'Verify and continue'}
              </Button>
            </>
          ) : (
            <Button variant="outline" onClick={loadFactor} className="w-full">Retry</Button>
          )}
          {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
          <Button variant="ghost" onClick={onSignOut} className="w-full">Sign out</Button>
        </CardContent>
      </Card>
    </main>
  );
}
